<template>
  <div class="p-6 dark:text-gray-50" v-if="(canAddBlackbox || user?.is_staff)">
    <div class="flex space-x-3 mb-6 items-center">
      <h1 class="text-2xl font-semibold dark:text-white">Add New Endpoint Availability {{ isMultiSubmit ? '(Optional)' : ''}}</h1>
      <div class="relative group">
      <button
        type="button"
        class="bg-transparent hover:bg-transparent"
        @click="startTutorial"
      >
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
      </button>
        <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </div>

    <form @submit.prevent="submitForm" class="space-y-4 p-6 glass-card">
      <div v-for="field in visibleFormFields" :key="field.key" class="flex flex-col">
        <label :for="field.key" class="mb-1 font-medium dark:text-white">{{
          field.label
        }}<span v-if="field.required" class="pl-1 text-red-500">*</span></label>
        <div v-if="field.type === 'searchableSelect'" class="relative">
          <div class="relative">
            <!-- Probe Input -->
            <input
              v-if="field.key === 'probe'"
              :id="field.key + '-search'"
              v-model="probeSearch"
              type="text"
              :placeholder="isProbeDisabled ? 'Not available for Central Monitoring' : (getSelectedProbeName || 'Search probes...')"
              class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              :disabled="isProbeDisabled"
              @focus="handleProbeFocus"
              @blur="handleProbeBlur"
              :class="[fieldErrors[field.key] ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500']"
            />
            
            <!-- Site Input -->
            <input
              v-else-if="field.key === 'site'"
              :id="field.key + '-search'"
              v-model="siteSearch"
              type="text"
              :placeholder="getSelectedSiteName || 'Search sites...'"
              class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              @focus="handleSiteFocus"
              @blur="handleSiteBlur"
              :class="[fieldErrors[field.key] ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500']"
            />
            <FieldError :message="fieldErrors[field.key]"/>
            
            <!-- Probe Dropdown -->
            <div 
              v-if="field.key === 'probe'"
              v-show="isProbeDropdownVisible" 
              class="absolute w-full mt-1 max-h-60 overflow-y-auto bg-white dark:bg-gray-600 dark:text-gray-50 border border-gray-300 rounded shadow-lg z-10"
            >
              <div 
                v-for="probe in filteredProbes"
                :key="probe.id"
                class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                @mousedown="selectProbe(probe)"
              >
                {{ probe.name }}
              </div>
              <div 
                v-if="filteredProbes.length === 0" 
                class="px-3 py-2 text-gray-500"
              >
                No probes found
              </div>
            </div>
            
            <!-- Site Dropdown -->
            <div 
              v-else-if="field.key === 'site'"
              v-show="isSiteDropdownVisible" 
              class="absolute w-full mt-1 max-h-60 overflow-y-auto bg-white dark:bg-gray-600 dark:text-gray-50 border border-gray-300 rounded shadow-lg z-10"
            >
              <div 
                v-for="site in filteredSites"
                :key="site.id"
                class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                @mousedown="selectSite(site)"
              >
                {{ site.name }}
              </div>
              <div 
                v-if="filteredSites.length === 0" 
                class="px-3 py-2 text-gray-500"
              >
                No sites found
              </div>
            </div>
          </div>
        </div>
        <GlassSelect
          v-else-if="field.type === 'select'"
          :id="field.key"
          v-model="formData[field.key]"
          @change="clearTargets"
          :options="field.options"
        />
        <input
          v-else-if="field.key !== 'targets'"
          :id="field.key"
          v-model="formData[field.key]"
          :type="field.type"
          :required="field.required"
          :class="[ 'px-3 py-2', fieldErrors[field.key]
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
            />
        
        <div v-if="field.key == 'targets'"
          >
          <input
            :id="field.key"
            v-model="formData[field.key]"
            @focus="clearFieldError(field.key)"
            rows="3"
            :class="['w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors[field.key]
                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                  : 'focus:ring-blue-500'
                ]"
            :placeholder="field.placeholder"
          >

          <FieldError :message="fieldErrors[field.key]"/>
          <div class="flex flex-wrap gap-2 mt-2">
            <div 
              v-for="target in targets"
              :key="target"
              class="group inline-flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
            >
              <span 
                v-show="targetAdded" 
                class="text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                {{ target }}
              </span>
              <button 
                type="button"
                @click="removeTarget(target)"
                class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 focus:outline-none transition-colors duration-200"
                aria-label="Remove target"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  class="h-4 w-4" 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                >
                  <path 
                    fill-rule="evenodd" 
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
                    clip-rule="evenodd" 
                  />
                </svg>
              </button>
            </div>
          </div>
          
          <button
            type="button"
            :class="{
              'btn bg-green-600 hover:bg-green-700 focus:ring-green-500': formData[field.key] !== null,
              'btn bg-gray-300 hover:bg-gray-400 dark:text-gray-900 focus:ring-gray-200': formData[field.key] === null,
              'btn bg-gray-300 text-white focus:outline-none focus:ring-2 transition duration-200 mt-2': true
            }"
            @click="addTarget(formData[field.key])"
            >Add Target
          </button>
        </div>
      </div>

      <div class="flex justify-end space-x-2">
        <button v-if="!isMultiSubmit"
          type="button"
          @click="goBack"
          class="btn-cancel"
        >
          Cancel
        </button>
        <button v-if="isMultiSubmit"
          type="button"
          @click="skipMultiSubmit"
          class="btn bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
        >
          Skip
        </button>
        <button
          id="submit-blackbox-btn"
          type="submit"
          class="btn bg-blue-600 hover:bg-blue-700"
        >
          Create Endpoint Availability
        </button>
      </div>
    </form>
  </div>
   <!-- Error Message -->
   <PageError v-if="permsLoaded && !canAddBlackbox && !user?.is_staff"
    :error="errorMessages['403']"
    :errorCode="'403'" 
  />
</template>

<script>
import { createBlackbox } from "@/services/blackboxService";
import GlassSelect from "@/components/GlassSelect.vue";
import { getAllProbes } from "@/services/probeService";
import { getSites } from "@/services/siteService";
import FieldError from "@/components/FieldError.vue";
import { useStore } from "@/store";
import PageError from "@/components/PageError.vue";
import { errorMessages } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useTutorial } from "@/composables/tutorial";
import { getOrgById } from "@/services/orgService";

export default {
  emits: ['blackbox-submitted', 'blackbox-skipped'],
  props: {
    isMultiSubmit: {
      type: Boolean,
      default: false
    },
    newMultiSubmitSite: {
    type: Number,
    default: null
    },
    newMultiSubmitProbe: {
    type: Number,
    default: null
    },
    submittedSite: { 
      type: Object, 
      default: null 
    },
    submittedProbe: { 
      type: Object, 
      default: null
    }
  },
  name: "AddBlackbox",
  components : {
    FieldError,
    GlassSelect,
    PageError,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      errorMessages: errorMessages,
      multiSubmitProbe: "",
      fieldErrors: {},
      formData: {
        module: "http_2xx",
        test_descriptor: "",
        targets: "",
        probe: null,
        site: null,
        job_name: "blackbox_job",
        scrape_interval: 60,
        scrape_timeout: 30,
      },
      targetAdded: false,
      targets: [],
      probes: [],
      sites: [],
      probeSearch: "",
      siteSearch: "",
      isProbeDropdownVisible: false,
      isSiteDropdownVisible: false,
      canAddBlackbox: false,
      addBlackboxTutorialSteps: [
        {
          element: '#probe-search',
          action: 'display',
          message: 'Search and select a probe. Use either probe or site — not both.'
        },
        {
          element: '#site-search',
          action: 'display',
          message: 'Or search and select a site instead of a probe.'
        },
        {
          element: '#module',
          action: 'display',
          message: 'Select the check module: HTTP, ICMP, UDP, or TCP.'
        },
        {
          element: '#test_descriptor',
          action: 'type',
          value: 'Tutorial check',
          message: 'Enter an optional descriptor for this check.'
        },
        {
          element: '#targets',
          action: 'display',
          message: 'Enter a target IP or URL, then click Add Target.'
        },
        {
          element: '#submit-blackbox-btn',
          action: 'display',
          message: 'Click to create the endpoint availability check.'
        },
      ],
      formFields: [
        {
          key: "probe",
          label: "Probe",
          type: "searchableSelect",
          required: false,
          filterFunction: (item, search) =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.id.toString().includes(search),
        },
        {
          key: "site",
          label: "Site",
          type: "searchableSelect",
          required: false,
          filterFunction: (item, search) =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.id.toString().includes(search),
        },
        {
          key: "module",
          label: "Module",
          type: "select",
          required: true,
          options: ["http_2xx", "icmp", "udp", "tcp_connect"],
        },
        {
          key: "test_descriptor",
          label: "Test Descriptor",
          type: "text",
          required: false,
          placeholder: "Test"
        },
        {
          key: "targets",
          label: "Target IP addresses or Endpoints",
          type: "text",
          required: true,
          placeholder: "Enter IP addresses or Endpoints",
        },
        {
          key: "scrape_interval",
          label: "Scrape Interval (seconds)",
          type: "number",
          required: true,
          staffOnly: true,
        },
        {
          key: "scrape_timeout",
          label: "Scrape Timeout (seconds)",
          type: "number",
          required: true,
          staffOnly: true,
        },
      ],
    };
  },
  computed: {
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    visibleFormFields() {
      return this.formFields.filter(f => !f.staffOnly || useStore.isStaff());
    },
    filteredProbes() {
    if (!this.probeSearch) {
      // If central monitoring multistep show no probes
      if (this.isMultiSubmit && !this.newMultiSubmitProbe) {
        return [];
      }
      // If in multi-step form, only show the specific probe in search
      if (this.isMultiSubmit && this.newMultiSubmitProbe) {
        return this.probes.filter(p => p.id === this.newMultiSubmitProbe);
      }
      return this.probes;
    }
    
    // Apply search filter
    const filtered = this.probes.filter((probe) =>
      probe.name.toLowerCase().includes(this.probeSearch.toLowerCase()) ||
      probe.id.toString().includes(this.probeSearch)
    );
    
    // If in multi-step form, filter to only the specific probe
    if (this.isMultiSubmit && this.newMultiSubmitProbe) {
      return filtered.filter(p => p.id === this.newMultiSubmitProbe);
    }
    
    return filtered;
  },
    isProbeDisabled() {
      // Disable probe field in Central Monitoring multi-step form
      return this.isMultiSubmit && !this.newMultiSubmitProbe;
    },

  filteredSites() {
    if (!this.siteSearch) {
      // If in multi-step form, only show the specific site in search
      if (this.isMultiSubmit && this.newMultiSubmitSite) {
        return this.sites.filter(s => s.id === this.newMultiSubmitSite);
      }
      return this.sites;
    }
    
    // Apply search filter
    const filtered = this.sites.filter((site) =>
      site.name.toLowerCase().includes(this.siteSearch.toLowerCase()) ||
      site.id.toString().includes(this.siteSearch)
    );

    // If in multi-step form, filter to only the specific site to appear in dropdown
    if (this.isMultiSubmit && this.newMultiSubmitSite) {
      return filtered.filter(s => s.id === this.newMultiSubmitSite);
    }
    
    return filtered;
  },
    
    getSelectedProbeName() {
      if (!this.formData.probe) return null;
      const selectedProbe = this.probes.find(p => p.id === this.formData.probe);
      return selectedProbe ? `${selectedProbe.name}` : null;
    },
    getSelectedSiteName() {
      if (!this.formData.site) return null;
      const selectedSite = this.sites.find(s => s.id === this.formData.site);
      return selectedSite ? `${selectedSite.name}` : null;
    },
    user(){
      return useStore.getUser();
    },
  },
  async mounted() {
    this.checkPermissions();
    try {
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = "Malformed request. Failed to create Endpoint Availability!";
        this.errorCode = "422!";
        return;
      }
      if (this.isMultiSubmit){
 
        // If in multi-submit mode, auto-select populate with submitted site and skip fetching all sites
        if (this.submittedSite) {
          this.sites = [this.submittedSite];
          this.selectSite(this.submittedSite);
        }
        
        if (this.submittedProbe){
          this.probes = [this.submittedProbe];
          this.selectProbe(this.submittedProbe);
        }
        return;
      }

      this.probes = await getAllProbes(validation.schemaName);
      this.sites = await getSites(validation.schemaName);
    } catch (error) {
        console.error("Error fetching probes/sites:", error);
    }
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
  },
  methods: {
    async startTutorial() {
      const originalFormData = { ...this.formData };
      await this._start(this.addBlackboxTutorialSteps, () => {
        this.formData = { ...originalFormData };
      });
    },
    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },
    async checkPermissions() {
      if (this.user) {
        this.canAddBlackbox = await useStore.hasPermission("Can add Blackbox");
      }
    },
    goBack(){
      this.$router.push({ 
        name: 'Blackboxes', 
        query: this.tenant.addSchemaParam() 
      });
    },
    async submitForm() {
      try {
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.error = "Malformed request. Failed to create Endpoint Availability!";
          this.errorCode = "422!";
          return;
        }

        this.fieldErrors = {};

        // Validate only probe or site is selected not both or none
        if (!this.formData.probe && !this.formData.site) {
          this.fieldErrors.probe = "Either Probe or Site must be selected";
          this.fieldErrors.site = "Either Probe or Site must be selected";
          return;
        }
        if (this.formData.probe && this.formData.site) {
          this.fieldErrors.probe = "Only one of Probe or Site can be selected";
          this.fieldErrors.site = "Only one of Probe or Site can be selected";
          return;
        }

        const blackboxData = { ...this.formData };
        blackboxData.targets = this.targets;
        
        const newBlackbox = await createBlackbox(blackboxData, validation.schemaName);

        if (this.isMultiSubmit){
          this.$emit('blackbox-submitted', blackboxData)
        } else {
          this.goBack();
        }

      } catch (error) {
        this.fieldErrors = {}; 
        const items = this.formFields;
        const errData = error?.response?.data;

        items.forEach(element => {
          const fieldError = errData?.[element.key];
          if (fieldError?.[0]) {
            this.fieldErrors[element.key] = fieldError[0]; 
          }
        });

        console.error("Error creating blackbox:", error);
      }
    },
    handleProbeFocus() {
      this.clearFieldError('probe')
      this.isProbeDropdownVisible = true;
      // Clear search when focusing to show all probes
      this.probeSearch = "";
    },
    handleProbeBlur() {
      setTimeout(() => {
        this.isProbeDropdownVisible = false;
      }, 200);
    },
    handleSiteFocus() {
      this.clearFieldError('site')
      this.isSiteDropdownVisible = true;
      this.siteSearch = "";
    },
    handleSiteBlur() {
      setTimeout(() => {
        this.isSiteDropdownVisible = false;
      }, 200);
    },
    selectProbe(probe) {
      this.formData.probe = probe.id;
      this.formData.site = null;
      this.isProbeDropdownVisible = false;
      this.probeSearch = "";
      delete this.fieldErrors.probe;
      delete this.fieldErrors.site;
    },
    selectSite(site) {
      this.formData.site = site.id;
      this.formData.probe = null;
      this.isSiteDropdownVisible = false;
      this.siteSearch = "";
      delete this.fieldErrors.probe;
      delete this.fieldErrors.site;
    },
    addTarget(target){
      if (target !== null) {
        this.fieldErrors = {};
        this.targetValidation(target)
        if (Object.keys(this.fieldErrors).length > 0) return;
        this.targets.push(target);
        this.targetAdded = true;
        this.formData.targets = null;
      }
    },
    targetValidation(target){
      if (this.formData.module === "tcp_connect"){
        const portMatch = target.match(/:(\d+)$/);
        if (!portMatch) {
          this.fieldErrors.targets = 'Target must include a port (e.g., example.com:443 or x.x.x.x:443)';
          return;
        }
        
        const port = parseInt(portMatch[1]);
        if (port < 1 || port > 65535) {
          this.fieldErrors.targets = 'Port must be between 1 and 65535';
        }

        if (target.startsWith('http://') || target.startsWith('https://')){
          this.fieldErrors.targets = 'Target should not include http:// or https://';
        }

      } else if (this.formData.module === "http_2xx"){

        if (!target.startsWith('http://') && !target.startsWith('https://')) {
           this.fieldErrors.targets = "Please add http:// or https:// (for example: https://example.com)";
           return;
        }
      }
    },
    clearTargets(){
      this.targets = [];
      this.formData.targets = [];
    },
    removeTarget(target){
      const index = this.targets.indexOf(target);
      this.targets.splice(index,1);
    },
    skipMultiSubmit(){
      this.$emit('blackbox-skipped','skipped')
    }
  },
};
</script>
